import { z } from 'zod'
import { EmailSchema, IsoDateTimeSchema, OkResponseSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'
import { ReasonCategorySchema } from '../domain/reasons'
import { DocumentCheckSchema, ManualVerificationSchema } from '../domain/document-policy'
import { LocationVerdictSchema } from './location'
import { EmailCodeSchema, RegistrationStageSchema, RegistrationStatusValueSchema } from './registration'

/**
 * Member area (Task 16; spec "Accounts and sessions", "Member dashboard", "Privacy and retention"; AC8, AC9, AC23):
 * S5 password, login, logout, the neutral password reset, change password, the dashboard card, the contact form,
 * the data export and erasure (by password, or by the emailed link for a registration without a password).
 *
 * Passwordless sign-in (docs/specs/2026-11-03-passwordless-design.md §1, §3-§5, rulings P1-P11): the sign-in email
 * (a link and a 6-digit code), the optional password with the "also ask for an email code" switch, and the
 * sign-in settings of the voter page.
 */

/**
 * How a voter account signs in (spec §1): `email` (no password: the sign-in email only), `password_or_email` (the
 * password alone, or the sign-in email), `password_and_email` (the password, then a 6-digit code emailed to them; the
 * sign-in email alone is refused).
 */
export const SIGN_IN_MODES = ['email', 'password_or_email', 'password_and_email'] as const
export const SignInModeSchema = z.enum(SIGN_IN_MODES)
export type SignInMode = z.infer<typeof SignInModeSchema>

/** The password as typed; the server applies the policy (length, common-password list) and answers 400. */

export const LoginBodySchema = z.object({ email: EmailSchema, password: z.string().min(1).max(256) })
export const ForgotPasswordBodySchema = z.object({ email: EmailSchema })
export const LinkTokenSchema = z.string().min(10).max(300)
export const ResetPasswordBodySchema = z.object({ token: LinkTokenSchema, password: z.string().min(1).max(256) })
/**
 * Change the password: the current password, or none within 10 minutes of a sign-in (`freshSignInUntil`); otherwise
 * 403 `fresh_sign_in_required`.
 */
export const ChangePasswordBodySchema = z.object({
  currentPassword: z.string().min(1).max(256).optional(),
  newPassword: z.string().min(1).max(256),
})

/** The sign-in email (spec §3): the same answer for every email. */
export const SignInRequestBodySchema = z.object({ email: EmailSchema })
/** The link of the sign-in email (`/login/link#t=…`): POSTed when the voter presses "Sign in" (P1), any device. */
export const SignInLinkBodySchema = z.object({ token: LinkTokenSchema })
/**
 * The 6-digit code of the sign-in email (works only in the browser that asked for it: the `ipc_signin_chal` cookie),
 * or of the password + code sign-in (the `ipc_login_chal` cookie).
 */
export const SignInCodeBodySchema = z.object({ code: EmailCodeSchema })

/** The "Sign-in" section of the voter page (spec §4). */
export const SignInSettingsSchema = z.strictObject({
  mode: SignInModeSchema,
  hasPassword: z.boolean(),
  /**
   * Until when this session counts as a fresh sign-in (10 minutes from the sign-in): setting a first password, a
   * change without the current password, the erasure and the export need it. Null once past.
   */
  freshSignInUntil: IsoDateTimeSchema.nullable(),
})
export type SignInSettings = z.infer<typeof SignInSettingsSchema>

/** A first password (no password yet; fresh sign-in): the mode becomes `password_or_email`. */
export const SetPasswordBodySchema = z.object({ password: z.string().min(1).max(256) })
/** Remove the password (P4: the current password): the mode becomes `email`. */
export const RemovePasswordBodySchema = z.object({ currentPassword: z.string().min(1).max(256) })
/**
 * The switch "Also ask for a code sent to my email when I sign in with my password" (password set only). On: the
 * session is enough. Off (P4): `currentPassword` is required.
 */
export const EmailCodeSettingBodySchema = z.object({
  enabled: z.boolean(),
  currentPassword: z.string().min(1).max(256).optional(),
})
/**
 * Passwordless P2: "Delete my data" with the current password,
 * or without one within 10 minutes of a sign-in (else 403 `fresh_sign_in_required`; the voter signs in by email
 * again or asks for an erasure link, `memberEraseLinkRequest`).
 */
export const MemberEraseBodySchema = z.object({ password: z.string().min(1).max(256).optional() })
/** An `erasure_links` token or a "continue my registration" token (`c1.…`). */
export const EraseLinkBodySchema = z.object({ token: LinkTokenSchema })

/** S6 / dashboard status card in plain words. */
export const MEMBER_CARDS = ['approved', 'review', 'not_accepted', 'needs_action'] as const
export const MemberCardSchema = z.enum(MEMBER_CARDS)
export type MemberCard = z.infer<typeof MemberCardSchema>

/** What we keep about the member ("My data": what we keep). */
export const MEMBER_KEEPS = [
  'name_and_email',
  'city',
  'consents',
  'location_evidence',
  'face_template',
  'face_check_results',
  'messages',
] as const
export const MemberKeepSchema = z.enum(MEMBER_KEEPS)

export const REJECT_REASON_CATEGORIES = ['age', 'place', 'face', 'duplicate', 'other'] as const
export const RejectReasonCategorySchema = z.enum(REJECT_REASON_CATEGORIES)

export const MemberMeSchema = z.object({
  card: MemberCardSchema,
  /** The registry id of the member's provider, whatever the card (the member pages resolve notices and the R32 retry from it). */
  providerId: z.string(),
  /** The dashboard sections ("My data"); null for `not_accepted` and `needs_action` (status card only). */
  member: z
    .object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      city: z.object({ name: z.string(), admin1: z.string().nullable(), country: z.string() }).nullable(),
      registeredAt: IsoDateTimeSchema,
      /** Display name of the provider that checked the member. */
      provider: z.string(),
      providerId: z.string(),
      keeps: z.array(MemberKeepSchema),
      /**
       * Identity document (spec §10.1, §11; final gate F3): the member gave the document consent and has results it
       * can withdraw ("My data" shows "Withdraw my document consent"). Absent otherwise.
       */
      documentWithdrawable: z.boolean().optional(),
    })
    .nullable(),
  /** Plain-language categories of a review (age, place, face, document). */
  reasonCategories: z.array(ReasonCategorySchema),
  /** `not_accepted` only: the reason category in plain words. */
  rejectReasonCategory: RejectReasonCategorySchema.optional(),
  /**
   * The member's enrollment was based on a checked identity document (by a provider, or by a person in a video call),
   * so a new face check needs no document (identity-document spec §12.1 step 7). Absent otherwise.
   */
  documentVerified: z.boolean().optional(),
})
export type MemberMe = z.infer<typeof MemberMeSchema>

/** `POST /api/member/login` for a `password_and_email` account: the password was right, a code was emailed. */
export const LoginCodeStepSchema = z.strictObject({ next: z.literal('code') })
/**
 * `POST /api/member/login`: signed in (the member cookie is set; the dashboard card, as before) or the code step
 * (`{ next: 'code' }`, the `ipc_login_chal` cookie is set; then `POST /api/member/login/code`). Tell them apart with
 * `'next' in body`.
 */
export const MemberLoginResponseSchema = z.union([LoginCodeStepSchema, MemberMeSchema])
export type MemberLoginResponse = z.infer<typeof MemberLoginResponseSchema>

export const MESSAGE_MAX = 2000
export const MessageBodySchema = z.object({ body: z.string().trim().min(1).max(MESSAGE_MAX) })
export const MemberMessageSchema = z.object({
  id: UuidSchema,
  direction: z.enum(['member', 'admin']),
  body: z.string(),
  createdAt: IsoDateTimeSchema,
})
export type MemberMessage = z.infer<typeof MemberMessageSchema>
export const MemberMessagesResponseSchema = z.object({ items: z.array(MemberMessageSchema) })
export const MemberMessageResponseSchema = z.object({ message: MemberMessageSchema })

const nullableDate = IsoDateTimeSchema.nullable()

/**
 * The photos kept for a person to review a registration (docs/specs/2026-11-03-review-media-design.md §1): the face
 * check frame, the document portrait and the document's front and back. The API's `review_media.kind` enum.
 */
export const REVIEW_MEDIA_KIND_IDS = ['live_face', 'doc_portrait', 'doc_front', 'doc_back'] as const
export const ReviewMediaKindSchema = z.enum(REVIEW_MEDIA_KIND_IDS)
export type ReviewMediaKindId = z.infer<typeof ReviewMediaKindSchema>

/** AC9: exactly these top-level keys; never a provider payload. */
export const MemberExportSchema = z.strictObject({
  declared: z.object({
    name: z.string().nullable(),
    email: z.string().nullable(),
    city: z
      .object({
        id: z.number().int(),
        name: z.string(),
        admin1: z.string().nullable(),
        country: z.string(),
        lat: z.number().nullable(),
        lng: z.number().nullable(),
      })
      .nullable(),
    cityNote: z.string().nullable(),
  }),
  consents: z.object({
    privacy: z.object({ at: nullableDate, version: z.string().nullable() }),
    biometric: z.object({ at: nullableDate, version: z.string().nullable(), provider: z.string().nullable() }),
  }),
  status: z.object({
    stage: RegistrationStageSchema,
    status: RegistrationStatusValueSchema.nullable(),
    provider: z.string(),
    registeredAt: IsoDateTimeSchema,
    emailVerifiedAt: nullableDate,
    locationSetAt: nullableDate,
    faceStartedAt: nullableDate,
    decidedAt: nullableDate,
    results: z.object({
      biometricConfidence: z.number().nullable(),
      liveness: z
        .object({
          decision: z.string(),
          score: z.number().nullable(),
          method: z.string().nullable(),
          warnings: z.array(z.string()),
        })
        .nullable(),
      ageEstimate: z.object({ years: z.number().nullable(), source: z.string(), band: z.string() }).nullable(),
      ageEstimateOwn: z.number().nullable(),
      uniqueness: z.object({ band: z.string(), similarity: z.number().nullable() }).nullable(),
    }),
  }),
  reasons: z.object({
    reviewReasons: z.array(z.string()),
    reasonCategories: z.array(ReasonCategorySchema),
    rejectReason: z.string().nullable(),
  }),
  location: z.object({
    confidence: z.number().nullable(),
    divergence: z.boolean().nullable(),
    divergenceKm: z.number().nullable(),
    /** The codes-only location verdict of the decision (identity-document spec §8.7). */
    verdict: LocationVerdictSchema.nullable(),
    ipObservations: z.array(
      z.object({
        step: z.string(),
        ip: z.string(),
        ipTruncated: z.boolean(),
        userAgent: z.string().nullable(),
        flags: z.record(z.string(), z.boolean()).nullable(),
        createdAt: IsoDateTimeSchema,
      })
    ),
    /** The device location answer (location v2: a fix with its rough point, or a refusal), with its diagnostics. */
    gps: z.array(
      z.object({
        kind: z.string(),
        /** The place the fix was measured against. */
        placeId: z.number().int().nullable(),
        lat: z.number().nullable(),
        lng: z.number().nullable(),
        accuracyM: z.number().nullable(),
        positionAt: IsoDateTimeSchema.nullable(),
        timeZone: z.string().nullable(),
        refusal: z.string().nullable(),
        diagnostics: z.record(z.string(), z.unknown()).nullable(),
        /** The fix-time measurement: distance to the place, nearest town, accuracy, age, the fix-time verdict. */
        measure: z.record(z.string(), z.unknown()).nullable(),
        createdAt: IsoDateTimeSchema,
      })
    ),
  }),
  messages: z.array(
    z.object({ direction: z.enum(['member', 'admin']), body: z.string(), createdAt: IsoDateTimeSchema })
  ),
  communications: z.array(z.object({ subject: z.string(), status: z.string(), sentAt: nullableDate })),
  /**
   * Identity document (spec §9, §10.1, §10.3): the decision record (clear codes), the document consent, the manual
   * verification record, and the birth year the decided detail keeps. No other document field: the provider data is
   * deleted, the rest of the details stays encrypted for the admin.
   */
  document: z.object({
    check: DocumentCheckSchema.nullable(),
    consent: z.object({ at: nullableDate, version: z.string().nullable(), provider: z.string().nullable() }),
    /** "Verified manually" (spec §10.3, §11 export): method, reviewing admin's id, when, the reasons it resolved. */
    manual: ManualVerificationSchema.nullable().default(null),
    /** The year of birth from the document (the year only, never the full date); null with no decided detail. */
    birthYear: z.number().int().nullable(),
  }),
  /**
   * Review media (review-media spec §5, Art. 15): the kinds of photo held for a person to review the registration,
   * when each was taken and the date it is deleted at the latest (earlier at the decision). Never the images.
   */
  reviewMedia: z.array(
    z.strictObject({ kind: ReviewMediaKindSchema, createdAt: IsoDateTimeSchema, expiresAt: IsoDateTimeSchema })
  ),
  /** The face template (Art. 15): base64 of little-endian float32, with its model id; null when none is stored. */
  template: z
    .object({
      modelId: z.string(),
      encoding: z.literal('base64-float32le'),
      dimensions: z.number().int(),
      vector: z.string(),
    })
    .nullable(),
})
export type MemberExport = z.infer<typeof MemberExportSchema>

export const memberRoutes = {
  /** Password sign-in (spec §4): the same 401 `invalid_credentials` for every failure. */
  memberLogin: route({
    method: 'POST',
    path: '/api/member/login',
    auth: 'public',
    body: LoginBodySchema,
    response: MemberLoginResponseSchema,
  }),
  /**
   * The code step of a `password_and_email` sign-in (P8): the code emailed by `memberLogin`, with the `ipc_login_chal`
   * cookie of that browser, while the password is unchanged. 5 wrong codes kill it (422 `invalid_code` with
   * `attemptsLeft`, then `code_attempts_exceeded`); 422 `code_expired` when it is gone (sign in with the password
   * again); 400 `invalid_challenge` without the cookie.
   */
  memberLoginCode: route({
    method: 'POST',
    path: '/api/member/login/code',
    auth: 'public',
    body: SignInCodeBodySchema,
    response: MemberMeSchema,
  }),
  /** Sign out (P3): ends every session of the voter (session_epoch + 1) and every open sign-in email. */
  memberLogout: route({ method: 'POST', path: '/api/member/logout', auth: 'public', response: OkResponseSchema }),
  /**
   * The sign-in email (spec §3, P7): `{ ok: true }` for every email (enumeration safety, 300 ms floor); 429
   * `rate_limited` past 5 per email or 20 per IP an hour. Sets the `ipc_signin_chal` cookie (path
   * /api/member/sign-in) for the code. A `password_and_email` account gets nothing (it signs in with the password).
   */
  memberSignInRequest: route({
    method: 'POST',
    path: '/api/member/sign-in/request',
    auth: 'public',
    body: SignInRequestBodySchema,
    response: OkResponseSchema,
  }),
  /**
   * The emailed link (`/login/link#t=…`, 15 min, single use, any device): signed in. 400 `invalid_link` when used,
   * expired, replaced by a newer email, or no longer allowed (the account now asks for password + code).
   */
  memberSignInLink: route({
    method: 'POST',
    path: '/api/member/sign-in/link',
    auth: 'public',
    body: SignInLinkBodySchema,
    response: MemberMeSchema,
  }),
  /**
   * The emailed code, only in the browser that asked for it (`ipc_signin_chal`): signed in. 422 `invalid_code` with
   * `attemptsLeft`, `code_attempts_exceeded` (5 per email), `code_expired`; 400 `invalid_challenge` without the cookie.
   */
  memberSignInCode: route({
    method: 'POST',
    path: '/api/member/sign-in/code',
    auth: 'public',
    body: SignInCodeBodySchema,
    response: MemberMeSchema,
  }),
  /**
   * "Forgot password" (P8): the same answer whether or not the email exists, the limits of the sign-in email. An
   * `email` or `password_or_email` account gets the sign-in email (and the `ipc_signin_chal` cookie is set); a
   * `password_and_email` account gets the reset link.
   */
  memberPasswordForgot: route({
    method: 'POST',
    path: '/api/member/password/forgot',
    auth: 'public',
    body: ForgotPasswordBodySchema,
    response: OkResponseSchema,
  }),
  /** The emailed reset link (`/reset#t=…`): 1 h, single use; no cookie, the mode never changes (P8). */
  memberPasswordReset: route({
    method: 'POST',
    path: '/api/member/password/reset',
    auth: 'public',
    body: ResetPasswordBodySchema,
    response: OkResponseSchema,
  }),
  /**
   * The "Sign-in" section (spec §4). Every change below ends the voter's other sessions (session_epoch + 1), keeps
   * this one (the cookie is re-issued), is audited and emails a notice; each answers the new settings. Wrong or
   * missing current password: 401 `invalid_credentials`; no fresh sign-in where one is needed: 403
   * `fresh_sign_in_required` (sign in by email again); the wrong state (a password already set, none to change or
   * remove): 409 `invalid_state_transition`.
   */
  memberSignInSettings: route({
    method: 'GET',
    path: '/api/member/account/sign-in',
    auth: 'member',
    response: SignInSettingsSchema,
  }),
  /** Set a first password (fresh sign-in within 10 minutes): mode `password_or_email`. */
  memberPasswordSet: route({
    method: 'POST',
    path: '/api/member/password/set',
    auth: 'member',
    body: SetPasswordBodySchema,
    response: SignInSettingsSchema,
  }),
  /** Change the password: the current one, or a fresh sign-in. The mode does not change. */
  memberPasswordChange: route({
    method: 'POST',
    path: '/api/member/password/change',
    auth: 'member',
    body: ChangePasswordBodySchema,
    response: SignInSettingsSchema,
  }),
  /** Remove the password (the current one, P4): mode `email`. */
  memberPasswordRemove: route({
    method: 'POST',
    path: '/api/member/password/remove',
    auth: 'member',
    body: RemovePasswordBodySchema,
    response: SignInSettingsSchema,
  }),
  /** The "also ask for an email code" switch: on → `password_and_email` (session), off → `password_or_email` (P4). */
  memberEmailCodeSetting: route({
    method: 'POST',
    path: '/api/member/account/sign-in/email-code',
    auth: 'member',
    body: EmailCodeSettingBodySchema,
    response: SignInSettingsSchema,
  }),
  memberMe: route({ method: 'GET', path: '/api/member/me', auth: 'member', response: MemberMeSchema }),
  memberMessages: route({
    method: 'GET',
    path: '/api/member/messages',
    auth: 'member',
    response: MemberMessagesResponseSchema,
  }),
  memberMessageSend: route({
    method: 'POST',
    path: '/api/member/messages',
    auth: 'member',
    body: MessageBodySchema,
    response: MemberMessageResponseSchema,
  }),
  /** Download my data (JSON attachment). */
  memberExport: route({ method: 'GET', path: '/api/member/export', auth: 'member', response: MemberExportSchema }),
  /**
   * Delete my data (P2): the current password, or none within 10 minutes of a sign-in. 401 `invalid_credentials`
   * (wrong password, or the session ended meanwhile); 403 `fresh_sign_in_required` (ask for an erasure link,
   * `memberEraseLinkRequest`, or sign in by email again).
   */
  memberErase: route({
    method: 'POST',
    path: '/api/member/erase',
    auth: 'member',
    body: MemberEraseBodySchema,
    response: OkResponseSchema,
  }),
  /**
   * Passwordless P2: email an erasure link (`delete_registration`, 1 h, single use) to the signed-in
   * voter; the link then goes through `memberEraseLink`.
   */
  memberEraseLinkRequest: route({
    method: 'POST',
    path: '/api/member/erase/link/request',
    auth: 'member',
    response: OkResponseSchema,
  }),
  /** Passwordless erasure (`/join/delete#t=…`): the emailed erasure link or a continue link. */
  memberEraseLink: route({
    method: 'POST',
    path: '/api/member/erase/link',
    auth: 'public',
    body: EraseLinkBodySchema,
    response: OkResponseSchema,
  }),
}
